import { isPlatformBrowser } from '@angular/common';
import { Component, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, Inject, OnInit, PLATFORM_ID, QueryList, Renderer2, ViewChild, ViewChildren, ViewEncapsulation, HostListener, DOCUMENT, NgZone, AfterViewInit } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { debounceTime, distinctUntilChanged, filter, forkJoin, fromEvent, map, Observable, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { MarketUtilsService } from '../../services/market-utils.service';
import { TypesenseService } from '../../services/typesense.service';
import { UserApiService } from '../../services/user-api.service';
import { Router } from '@angular/router';
import { AssetSearchModalService, SearchResult } from '../../services/asset-search-modal.service';
import { AuthService } from '../../services/auth.service';

type AssetSearchResultViewModel = SearchResult & {
   complianceKey: string;
   complianceText: string;
   ratingKey: number;
   ratingText: string;
};

@Component({
    selector: 'app-asset-search-modal',
    templateUrl: './asset-search-modal.component.html',
    styleUrls: ['./asset-search-modal.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AssetSearchModalComponent implements OnInit, AfterViewInit {

   private destroy$ = new Subject<void>();
   isMobileDevice = false;
   searchStocksAndEtfs!: FormGroup;
   private bodyScrollLocked = false;
   loaderItems = Array.from({ length: 16 }, (_, i) => i + 1);

   @ViewChild("searchInput")
   searchInput!: ElementRef<HTMLInputElement>;

   showPopular = false;
   searching = false;
   isSearchMode = false;
   dataAvailable = false;
   private navigating = false;

   stockCountries: string[] = [];
   etfCountries: string[] = [];
   userCountry = "";

   loadingTopAssets = false;
   topStocks: AssetSearchResultViewModel[] = [];
   topEtfs: AssetSearchResultViewModel[] = [];
   popularAssets: AssetSearchResultViewModel[] = [];

   searchResults: AssetSearchResultViewModel[] = [];
   filteredResults: AssetSearchResultViewModel[] = [];

   assetType: "ALL" | "STOCK" | "ETF" = "ALL";

   activeIndex = 0;
   
   // Context Menu State
   contextMenuVisible = false;
   contextMenuPosition = { x: 0, y: 0 };
   contextMenuItem: SearchResult | null = null;

   @ViewChild('clearBtn') clearBtn!: ElementRef<HTMLButtonElement>;
   @ViewChild('closeBtn') closeBtn!: ElementRef<HTMLButtonElement>;
   @ViewChild('allTab') allTab!: ElementRef<HTMLButtonElement>;
   @ViewChild('stocksTab') stocksTab!: ElementRef<HTMLButtonElement>;
   @ViewChild('etfsTab') etfsTab!: ElementRef<HTMLButtonElement>;

   @ViewChildren('resultItem')
   resultItems!: QueryList<ElementRef<HTMLElement>>;

   @ViewChild('resultsContainer')
   resultsContainer!: ElementRef<HTMLElement>;

   complianceStatusMap: Record<string, string> = {
      COMPLIANT: 'Halal',
      NON_COMPLIANT: 'Not Halal',
      QUESTIONABLE: 'Doubtful',
      NOT_UNDER_COVERAGE: 'Not Covered',
   };

   complainceRatingMap: Record<number, string> = {
      9: 'A+',
      8: 'A',
      7: 'A-',
      6: 'B+',
      5: 'B',
      4: 'B-',
      3: 'C+',
      2: 'C',
      1: 'C-',
      0: '-',
   };

   constructor(
      public activeModal: NgbActiveModal,
      private fb: FormBuilder,
      private marketUtilsService: MarketUtilsService,
      private userApiService: UserApiService,
      private router: Router,
      private assetSearchModalService: AssetSearchModalService,
      private renderer: Renderer2,
      public cdr: ChangeDetectorRef,
      private authService: AuthService,
      private ngZone: NgZone,
      @Inject(DOCUMENT) private document: Document,
      @Inject(PLATFORM_ID) private platformId: Object
   ) { }

   ngOnInit(): void {
      this.isMobileDevice =
         isPlatformBrowser(this.platformId) &&
         /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      this.searchStocksAndEtfs = this.fb.group({
         stockName: [""]
      });

      this.lockBodyScroll();

      this.ngZone.runOutsideAngular(() => {
         this.init();
         this.getUserDetails();
         this.loadPopularAssets();
      });
   }

   ngAfterViewInit(): void {
      if (!isPlatformBrowser(this.platformId)) return;

      // Manual host listeners to avoid zone-patched triggers on high-frequency events
      this.ngZone.runOutsideAngular(() => {
         // 1. Initial UI Setup
         if (!this.isMobileDevice) {
            this.searchInput?.nativeElement.focus();
         }

         // 2. Search logic initialization
         this.setupSearchListener();

         // 1. Global Keydown on Modal
         const modalEl = this.document.querySelector('.stocks-etfs-search-modal');
         if (modalEl) {
            fromEvent<KeyboardEvent>(modalEl, 'keydown').pipe(
               takeUntil(this.destroy$)
            ).subscribe(event => this.onKeyDown(event));
         }

         // 2. Click Delegation on Results Container
         if (this.resultsContainer) {
            // Hover logic (already manual)
            fromEvent<MouseEvent>(this.resultsContainer.nativeElement, 'mouseover').pipe(
               takeUntil(this.destroy$)
            ).subscribe(event => {
               const target = event.target as HTMLElement;
               const resultItem = target.closest('.result-item') as HTMLElement;
               if (resultItem && resultItem.dataset['index']) {
                  const index = parseInt(resultItem.dataset['index'], 10);
                  if (this.activeIndex !== index) {
                     this.setActiveIndex(index);
                  }
               }
            });

            // Click & Context Menu logic
            fromEvent<MouseEvent>(this.resultsContainer.nativeElement, 'click').pipe(
               takeUntil(this.destroy$)
            ).subscribe(event => {
               const target = event.target as HTMLElement;

               // Check for Pricing/Locked Trigger (delegation)
               const pricingBtn = target.closest('.pricing-trigger');
               if (pricingBtn) {
                  this.navigateToPricing(event);
                  return;
               }

               // Check for Result Item click
               const resultItem = target.closest('.result-item') as HTMLElement;
               if (resultItem && resultItem.dataset['index']) {
                  const index = parseInt(resultItem.dataset['index'], 10);
                  const item = this.filteredResults[index];
                  if (item) this.navigateToItem(item);
               }
            });

            fromEvent<MouseEvent>(this.resultsContainer.nativeElement, 'contextmenu').pipe(
               takeUntil(this.destroy$)
            ).subscribe(event => {
               const target = event.target as HTMLElement;
               const resultItem = target.closest('.result-item') as HTMLElement;
               if (resultItem && resultItem.dataset['index']) {
                  const index = parseInt(resultItem.dataset['index'], 10);
                  const item = this.filteredResults[index];
                  if (item) this.onRightClick(event, item);
               }
            });
         }

         // 3. Header & Tab Actions
         if (this.clearBtn) fromEvent(this.clearBtn.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => this.clearSearch());
         if (this.closeBtn) fromEvent(this.closeBtn.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => this.close());
         
         if (this.allTab)    fromEvent(this.allTab.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => this.setAssetType('ALL'));
         if (this.stocksTab) fromEvent(this.stocksTab.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => this.setAssetType('STOCK'));
         if (this.etfsTab)   fromEvent(this.etfsTab.nativeElement, 'click').pipe(takeUntil(this.destroy$)).subscribe(() => this.setAssetType('ETF'));

         // 4. Global Document Click (Manual replaces @HostListener)
         fromEvent<MouseEvent>(this.document, 'click').pipe(
            takeUntil(this.destroy$)
         ).subscribe((event: MouseEvent) => {
            if (!this.contextMenuVisible) return;

            const target = event.target as HTMLElement;
            
            // Check if click is inside the context menu
            const isInsideMenu = !!target.closest('.custom-context-menu');
            
            // If clicking the action button ("Open in new tab"), handle it
            const openTabBtn = target.closest('#openNewTabBtn') || (target.tagName === 'LI' && target.innerText.includes('Open in new tab'));
            if (openTabBtn) {
               this.openInNewTab();
               return; 
            }

            // Original behavior: clicking INSIDE the menu (but not on the button) didn't close it due to stopPropagation
            if (isInsideMenu) return;

            this.closeContextMenu();
         });
      });
   }

   ngOnDestroy(): void {
      this.unlockBodyScroll();
      this.destroy$.next();
      this.destroy$.complete();
   }

   /* ================= POPULAR ASSET ================= */

   private loadPopularAssets(): void {
      this.loadingTopAssets = true;

      forkJoin([
         this.assetSearchModalService.getTopStocks(),
         this.assetSearchModalService.getTopETFs(),
      ])
         .pipe(takeUntil(this.destroy$))
         .subscribe({
            next: ([stocks, etfs]) => {
               this.topStocks = this.normalizeCompliance(stocks);
               this.topEtfs   = this.normalizeCompliance(etfs);

               // SEQUENCE:
               // 1. Accessible Stocks
               // 2. Accessible ETFs
               // 3. Non Accessible Stocks & ETFs

               const accessibleStocks = this.topStocks.filter(s => s.isAccessible);
               const accessibleEtfs   = this.topEtfs.filter(e => e.isAccessible);
               
               const otherStocks = this.topStocks.filter(s => !s.isAccessible);
               const otherEtfs   = this.topEtfs.filter(e => !e.isAccessible);

               this.popularAssets = [
                  ...accessibleStocks,
                  ...accessibleEtfs,
                  ...otherStocks,
                  ...otherEtfs
               ];

               this.searchResults = [...this.popularAssets];
               this.applyAssetFilter();

               this.dataAvailable = this.filteredResults.length > 0;
               this.isSearchMode = false;
               this.loadingTopAssets = false;
               this.cdr.detectChanges();
            },
            error: () => {
               this.loadingTopAssets = false;
               this.cdr.detectChanges();
            }
         });
   }

   /* ================= SEARCH ================= */

   private setupSearchListener(): void {
      fromEvent(this.searchInput.nativeElement, 'input')
         .pipe(
            map(() => this.searchInput.nativeElement.value.trim()),
            debounceTime(300),
            distinctUntilChanged(),
            tap(term => {
               if (!term) {
                  this.isSearchMode = false;
                  this.searching = false;
                  this.searchResults = [...this.popularAssets];
                  this.applyAssetFilter();
                  this.dataAvailable = this.filteredResults.length > 0;
               } else {
                  this.isSearchMode = true;
                  this.searching = true;
               }
               
               // Update form control silently to keep state in sync without triggering zone
               this.searchStocksAndEtfs.get('stockName')?.setValue(term, { emitEvent: false });
               this.cdr.detectChanges();
            }),
            filter(term => term.length > 0),
            switchMap(term =>
               this.performSearch(term).pipe(
                  tap(results => {
                     this.searchResults = this.normalizeCompliance(results);

                     if (!this.authService.isLoggedIn()) {
                        this.searchResults = this.sortResultsByAccessSequence(this.searchResults);
                     }

                     this.applyAssetFilter();
                     this.dataAvailable = this.filteredResults.length > 0;
                     this.searching = false;
                     this.cdr.detectChanges();
                  })
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe({
            error: () => {
               this.searching = false;
               this.cdr.detectChanges();
            }
         });
   }

   private performSearch(term: string): Observable<SearchResult[]> {
      return this.assetSearchModalService
         .searchStocksAndEtfs(
            term,
            this.stockCountries,
            this.etfCountries,
            this.userCountry
         )
         .pipe(
            map((results: SearchResult[]) => {
               if (!results || !results.length) {
                  return [];
               }
               return this.userCountry ? this.sortData(results) : results;
            })
         );
   }

   private normalizeCompliance( results: SearchResult[] ): AssetSearchResultViewModel[] {
      return results.map(item => {
         // ---------- COMPLIANCE ----------
         const complianceKey =
            item.complianceStatus &&
               this.complianceStatusMap[item.complianceStatus]
               ? item.complianceStatus
               : 'NOT_UNDER_COVERAGE';

         
         // ---------- RATING (search uses ranking, top assets may use complianceRating) ----------
         const rawRating = Number(
            (item as any).ranking ?? (item as any).complianceRating
         );
         const ratingKey =
            Number.isInteger(rawRating) && this.complainceRatingMap[rawRating] !== undefined
               ? rawRating
               : 0;

         return {
            ...item,
            complianceKey,
            complianceText: this.complianceStatusMap[complianceKey],
            ratingKey,
            ratingText: this.complainceRatingMap[ratingKey],
         };
      });
   }

   trackBySearchResult(index: number, item: AssetSearchResultViewModel): string {
      return item.stockName;
   }

   trackByIndex(index: number): number {
      return index;
   }

   /* ================= FILTER ================= */

   setAssetType(type: "ALL" | "STOCK" | "ETF"): void {
      this.assetType = type;
      this.applyAssetFilter();
      this.dataAvailable = this.filteredResults.length > 0;
      this.cdr.detectChanges();
   }

   private applyAssetFilter(): void {
      this.filteredResults =
         this.assetType === "ALL"
            ? this.searchResults
            : this.searchResults.filter(r => r.type === this.assetType);

      this.activeIndex = this.filteredResults.length ? 0 : -1;
   }

   get noResultsMessage(): string {
      switch (this.assetType) {
         case 'STOCK':
            return 'Try searching for a different stock';
         case 'ETF':
            return 'Try searching for a different ETF';
         default:
            return 'Try searching for a different stock or ETF';
      }
   }

   /* ================= UI ================= */

   clearSearch(): void {
      this.searchStocksAndEtfs.patchValue(
         { stockName: "" }
      );
      this.resetResults();
   }

   resetResults(): void {
      this.isSearchMode = false;
      this.searching = false;

      this.searchResults = [...this.popularAssets];

      this.applyAssetFilter();
      this.dataAvailable = this.filteredResults.length > 0;
      this.cdr.detectChanges();
   }

   close(): void {
      this.unlockBodyScroll();
      this.activeModal.close();
   }

   /* ================= NAVIGATION ================= */

   navigateToItem(item: SearchResult): void {
      this.unlockBodyScroll();
      this.navigating = true;
      this.activeModal.close();

      const route = this.getItemRoute(item);

      this.router.navigate(route).then(() => {
         this.searchStocksAndEtfs.reset({ stockName: "" });
         this.resetResults();
         this.navigating = false;
      });
   }

   openSignupPopup() {
      this.authService.openAuthPopup(null, null, 'signUp', 'waitlist');
   }

   navigateToPricing(event: MouseEvent): void {
      event.stopPropagation();
      this.close();
      if (!this.authService.isLoggedIn()) {
         setTimeout(() => {
            this.openSignupPopup();
         }, 100);
         return;
      }
      this.router.navigate(['/pricing']);
   }

   /* ================= DATA ================= */

   /**
    * Sorts assets in specific order:
    * 1. Accessible Stocks
    * 2. Accessible ETFs
    * 3. Non-Accessible Items
    */
   sortResultsByAccessSequence(data: AssetSearchResultViewModel[]): AssetSearchResultViewModel[] {
      const accessibleStocks = data.filter(s => s.isAccessible && s.type === 'STOCK');
      const accessibleEtfs   = data.filter(s => s.isAccessible && s.type === 'ETF');
      const others           = data.filter(s => !s.isAccessible);

      return [
         ...accessibleStocks,
         ...accessibleEtfs,
         ...others
      ];
   }

   sortData(data: SearchResult[]): SearchResult[] {
      const top = data.filter(d => d.countryName === this.userCountry);
      const rest = data.filter(d => d.countryName !== this.userCountry);

      return [...top, ...rest].filter(
         (v, i, self) =>
            i === self.findIndex(t => t.stockName === v.stockName)
      );
   }

   async getUserDetails(): Promise<void> {
      const data = await this.userApiService.fetchUserDetails();
      if (data) {
         this.userCountry = data.geo_ip_country;
      }
      this.cdr.detectChanges();
   }

   async init(): Promise<void> {
      if (!isPlatformBrowser(this.platformId)) return;

      const countries = await this.marketUtilsService.fetchCountries() as {
         countryCode: string;
         stockEnabled: boolean;
         etfEnabled: boolean;
      }[];

      if (!Array.isArray(countries)) return;

      this.stockCountries = countries
         .filter(c => c.stockEnabled)
         .map(c => c.countryCode);

      this.etfCountries = countries
         .filter(c => c.etfEnabled)
         .map(c => c.countryCode);
         
      this.cdr.detectChanges();
   }

   onKeyDown(event: KeyboardEvent): void {
      const target = event.target as HTMLElement;

      // Ignore navigation keys while typing unless results exist
      if ( target.tagName === 'INPUT' && !this.filteredResults.length ) {
        return;
      }

      if (!this.filteredResults.length) return;

      switch (event.key) {
         case 'ArrowDown':
            event.preventDefault();
            this.activeIndex = (this.activeIndex + 1) % this.filteredResults.length;
            this.scrollIntoView();
            break;

            case 'ArrowUp':
            event.preventDefault();
            this.activeIndex = (this.activeIndex - 1 + this.filteredResults.length) % this.filteredResults.length;
            this.scrollIntoView();
            break;

         case 'Enter':
            event.preventDefault();
            const item = this.filteredResults[this.activeIndex];
            if (item) {
               this.navigateToItem(item);
            }
            break;

         case 'Escape':
            this.close();
            break;
      }
      this.cdr.detectChanges();
   }

   setActiveIndex(index: number): void {
      this.activeIndex = index;
      this.cdr.detectChanges();
   }

   private scrollIntoView(): void {
      queueMicrotask(() => {
         if (
            !this.resultItems ||
            !this.resultsContainer ||
            this.activeIndex < 0
         ) {
            return;
         }

         const container = this.resultsContainer.nativeElement;
         const item = this.resultItems.toArray()[this.activeIndex]?.nativeElement;

         if (!item) return;

         const TOP_OFFSET = 40;
         const BOTTOM_OFFSET = 16;

         const containerTop = container.scrollTop + TOP_OFFSET;
         const containerBottom = container.scrollTop + container.clientHeight - BOTTOM_OFFSET;

         const itemTop = item.offsetTop;
         const itemBottom = itemTop + item.offsetHeight;

         if (itemTop < containerTop) {
            container.scrollTo({
               top: itemTop - TOP_OFFSET,
            });
         } else if (itemBottom > containerBottom) {
            container.scrollTo({
               top:
                  itemBottom -
                  container.clientHeight +
                  BOTTOM_OFFSET,
            });
         }
      });
   }

   /* ================= CONTEXT MENU ================= */

   onRightClick(event: MouseEvent, item: SearchResult): void {
      event.preventDefault();
      event.stopPropagation();
      this.contextMenuVisible = true;
      this.contextMenuItem = item;
      this.contextMenuPosition = { x: event.clientX, y: event.clientY };
      this.cdr.detectChanges();
   }

   closeContextMenu(): void {
      this.contextMenuVisible = false;
      this.cdr.detectChanges();
   }

   openInNewTab(): void {
      if (this.contextMenuItem) {
         const route = this.getItemRoute(this.contextMenuItem);
         const url = this.router.serializeUrl(this.router.createUrlTree(route));
         window.open(url, '_blank');
      }
      this.closeContextMenu();
   }

   getItemRoute(item: SearchResult): any[] {
      switch (item.type) {
         case "ETF":
            return ["/etf", item.stockName];
         case "IPO":
            return ["/ipo-data", item.stockName];
         default:
            return ["/stock", item.stockName];
      }
   }




   private lockBodyScroll(): void {
      if (!isPlatformBrowser(this.platformId) || this.bodyScrollLocked) return;

      this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      this.bodyScrollLocked = true;
   }

   private unlockBodyScroll(): void {
      if (!isPlatformBrowser(this.platformId) || !this.bodyScrollLocked) return;

      this.renderer.removeStyle(this.document.body, 'overflow');
      this.bodyScrollLocked = false;
   }
}
